import React, { useState, useEffect } from 'react';
import { useChat } from './ChatProvider';
import {
  Box,
  IconButton,
  Flex,
  Text,
  useDisclosure,
  Badge,
  VStack,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabIndicator,
} from '@chakra-ui/react';
import { FaPaperPlane, FaArrowLeft, FaChevronDown, FaComments, FaEdit, FaUsers, FaHeadset, FaArchive } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { UsersTab } from './components/UsersTab';
import { ChatMessages } from './components/ChatMessages';
import { GroupChatDialog } from './components/GroupChatDialog';
import { GroupChatMembersDialog } from './components/GroupChatMembersDialog';
import { User } from './types';
import { useUserStore } from '../../store/userStore';
import UserAvatar from '../../components/UserAvatar';

const MotionBox = motion.create(Box);
const MotionIconButton = motion.create(IconButton);

const PRIMARY_GREEN = '#002e0b';
const BLACK = '#000';

const colorPalette = ["red", "blue", "green", "yellow", "purple", "orange"];

const pickPalette = (name: string) => {
  const index = name.charCodeAt(0) % colorPalette.length;
  return colorPalette[index];
};

export const ChatWindow: React.FC = () => {
  const { messages, activeRoom, setActiveRoom, sendMessage, rooms, users, createDirectChat, joinDirectChat, currentUserId, totalUnread, markedUsers, joinRoom, groupChats, supportChats, setRooms, socket } = useChat();
  const [isOpen, setIsOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const { isOpen: isGroupChatOpen, onOpen: onGroupChatOpen, onClose: onGroupChatClose } = useDisclosure();
  const { user: currentUser } = useUserStore();
  const [isGroupChat, setIsGroupChat] = useState(false);
  const [groupChatName, setGroupChatName] = useState('');
  const [groupChatMembers, setGroupChatMembers] = useState<User[]>([]);
  const [isGroupChatMembersOpen, setIsGroupChatMembersOpen] = useState(false);
  const [isSupportChat, setIsSupportChat] = useState(false);
  const [activeTab, setActiveTab] = useState<'chats' | 'support'>('chats');
  const isSupportUser = currentUser?.role?.toUpperCase() === 'SUPPORT' || currentUser?.role?.toUpperCase() === 'ADMIN';
  const bgColor = BLACK;
  const borderColor = PRIMARY_GREEN;
  const hoverBg = '#111';
  const messageBg = '#111';
  const userMessageBg = PRIMARY_GREEN;
  const inputBg = '#222';
  const textColor = 'white';
  const secondaryText = 'gray.300';
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;
    sendMessage(newMessage, selectedRoom);
    setRooms(prev => prev.map(room => room.id === selectedRoom ? { ...room, last_message_at: new Date().toISOString() } : room));
    setNewMessage('');
  };

  const handleBackClick = () => {
    setActiveRoom(null);
    setIsGroupChat(false);
    setGroupChatMembers([]);
    setGroupChatName('');
    setSelectedRoom(null);
    setNewMessage('');
  };

  const handleArchiveRoom = () => {
    if (!socket || !selectedRoom) return;
    socket.emit('room:archive', { roomId: selectedRoom });
    handleBackClick();
  };

  const handleCreateGroupChat = (userIds: string[], chatName: string) => {
    if (userIds.length === 0 || !chatName) return;

    // Get all user IDs including current room members and newly selected users
    const allUserIds = [...new Set([
      currentUser?.id,
      ...userIds
    ])];

    // Create a new group chat with all users
    joinRoom(
      '', // Empty ID for new room
      `${chatName}`,
      'group',
      'Group chat',
      allUserIds
    );
    setIsGroupChat(true);
    setGroupChatName(chatName);
  };

  const joinExistingGroupChat = (chatRoomId: string, members: string[], chatName: string, users: User[]) => {
    setSelectedRoom(chatRoomId);
    setIsGroupChat(true);
    setGroupChatName(chatName);
    const otherUsers = users.filter((user: User) => user.id !== currentUser?.id);
    setGroupChatMembers(otherUsers);
    joinRoom(chatRoomId, '', 'group', '', members);
    //check if room is unread
    const room = rooms.find(r => r.id === chatRoomId);
    if (room?.unread) {
      setRooms(prev => prev.map(room => room.id === chatRoomId ? { ...room, unread: false } : room));
    }
    if (room?.type === 'support') {
      setIsSupportChat(true);
    }
    else {
      setIsSupportChat(false);
    }
  };

  const handleJoinDirectChat = (roomId: string) => {
    joinDirectChat(roomId);
    //check if room is unread
    const room = rooms.find(r => r.id === roomId);
    if (room?.unread) {
      setRooms(prev => prev.map(room => room.id === roomId ? { ...room, unread: false } : room));
    }
    setSelectedRoom(roomId);
  };

  useEffect(() => {
    setFilteredUsers(users.filter(user => {
      const searchLower = searchQuery.toLowerCase();
      return (
        user.username?.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      );
    }));
  }, [searchQuery, users]);

  useEffect(() => {
    if (activeRoom) {
      setSelectedRoom(activeRoom);
    }
  }, [activeRoom]);
  return (
    <React.Fragment>
      <AnimatePresence>
        {!isOpen ? (
          <Box position="relative" display="inline-block">
            <MotionIconButton
              aria-label="Open chat"
              position="fixed"
              bottom={4}
              right={4}
              size="lg"
              borderRadius="full"
              onClick={() => setIsOpen(true)}
              zIndex={1000}
              boxShadow="lg"
              background="#002e0b"
              color="white"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              _hover={{ bg: '#004d1a' }}
              icon={<FaComments />}
            />
            {totalUnread > 0 && (
              <Badge
                aria-label="Unread messages"
                variant="solid"
                colorScheme="red"
                position="fixed"
                bottom="35px" // Adjusted for better positioning
                right="25px" // Adjusted for better positioning
                transform="translate(50%, -50%)" // This is key for circular buttons
                background="red.500"
                color="white"
                borderRadius="full"
                minW="20px"
                h="20px"
                id="unread-badge"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="xs"
                fontWeight="bold"
                zIndex={1000}
                boxShadow="0 0 0 2px white"
                px={1}
              >
                {totalUnread > 99 ? '99+' : totalUnread} {/* Display actual count */}
              </Badge>
            )}
          </Box>
        ) : (
          <MotionBox
            position="fixed"
            bottom={4}
            right={4}
            w="400px"
            h="600px"
            bg={bgColor}
            borderRadius="2xl"
            boxShadow="2xl"
            zIndex={1000}
            display="flex"
            flexDirection="column"
            borderWidth="2px"
            borderColor={borderColor}
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            color={textColor}
          >
            {/* Header */}
            <Flex
              p={4}
              borderBottomWidth="1px"
              borderColor={borderColor}
              justify="space-between"
              align="center"
              bg={PRIMARY_GREEN}
              borderTopRadius="2xl"
            >
              <Flex align="center" gap={2}>
                <Text fontWeight="bold" fontSize="lg" color={textColor}>Messages</Text>
              </Flex>
              <IconButton
                aria-label="Close chat"
                variant="ghost"
                bg="whiteAlpha.100"
                size="sm"
                onClick={() => setIsOpen(false)}
                color={textColor}
                _hover={{ bg: 'whiteAlpha.200' }}
                _active={{ boxShadow: 'none', outline: 'none' }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
                _before={{
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  bg: 'blackAlpha.200',
                  borderRadius: 'md',
                  zIndex: -1
                }}
              >
                <FaChevronDown />
              </IconButton>
            </Flex>

            {/* Main Content: Users list or Chat */}
            {!selectedRoom ? (
              <Box flex="1" overflow="hidden" display="flex" flexDirection="column">
                {isSupportUser && (
                  <Tabs
                    index={activeTab === 'chats' ? 0 : 1}
                    onChange={(index) => setActiveTab(index === 0 ? 'chats' : 'support')}
                    variant="unstyled"
                    px={4}
                    pt={2}
                  >
                    <TabList
                      bg="#111"
                      borderRadius="lg"
                      p={1}
                      gap={1}
                    >
                      <Tab
                        flex={1}
                        borderRadius="md"
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.400"
                        _selected={{ bg: PRIMARY_GREEN, color: 'white' }}
                        py={2}
                      >
                        <HStack gap={2}>
                          <FaComments size={14} />
                          <Text>Chats</Text>
                        </HStack>
                      </Tab>
                      <Tab
                        flex={1}
                        borderRadius="md"
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.400"
                        _selected={{ bg: PRIMARY_GREEN, color: 'white' }}
                        py={2}
                        position="relative"
                      >
                        <HStack gap={2}>
                          <FaHeadset size={14} />
                          <Text>Support</Text>
                          {supportChats.some(r => r.unread) && (
                            <Box
                              w="2"
                              h="2"
                              borderRadius="full"
                              bg="red.500"
                            />
                          )}
                        </HStack>
                      </Tab>
                    </TabList>
                  </Tabs>
                )}
                <UsersTab
                  markedUsers={markedUsers}
                  users={filteredUsers}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  hoverBg={hoverBg}
                  textColor={textColor}
                  secondaryText={secondaryText}
                  inputBg={inputBg}
                  onGroupChatOpen={onGroupChatOpen}
                  pickPalette={pickPalette}
                  groupChats={activeTab === 'support' ? [] : groupChats}
                  rooms={isSupportUser ? (activeTab === 'support' ? supportChats : rooms.filter(r => r.type !== 'support')) : rooms}
                  joinExistingGroupChat={joinExistingGroupChat}
                  onJoinDirectChat={handleJoinDirectChat}
                  selectedRoom={selectedRoom}
                  hideCreateButton={activeTab === 'support'}
                />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" flex="1" h="100%" minH="0" overflow="hidden">
                <Flex align="center" justify="space-between" p={2} borderBottomWidth="1px" borderColor={borderColor} bg={bgColor} flexShrink={0}>
                  <Flex align="center">

                    <IconButton
                      aria-label="Back to users"
                      variant="ghost"
                      bg="transparent"
                      onClick={handleBackClick}
                      color={textColor}
                      _hover={{ bg: hoverBg }}
                      mr={2}
                    >
                      <FaArrowLeft />
                    </IconButton>
                    <Text fontWeight="medium" color={textColor}>
                      {(() => {
                        if (isGroupChat) {
                          return groupChatName;
                        }
                        const room = rooms.find(r => r.id === selectedRoom);
                        if (!room) return 'Chat';
                        const otherUserId = room.members.find((id: string) => id !== currentUserId);
                        const otherUser = users.find(u => u.id === otherUserId);
                        return otherUser?.username || otherUser?.email || 'Chat';
                      })()}
                    </Text>
                  </Flex>
                  {isGroupChat && groupChatMembers && groupChatMembers.length > 0 && !isSupportChat && (
                    //list members of group chat, if too long, add a show more button
                    <HStack gap={2}>
                      {groupChatMembers.slice(0, 3).map((member) => (
                        <UserAvatar key={member.id} user={member} size="sm" canEdit={false} />
                      ))}
                      {groupChatMembers.length > 3 && (
                        <IconButton
                          aria-label="View all group members"
                          variant="ghost"
                          bg="transparent"
                          onClick={() => setIsGroupChatMembersOpen(true)}
                          color={textColor}
                          size="sm"
                          fontSize="lg"
                          _hover={{ bg: hoverBg }}
                        >
                          ...
                        </IconButton>
                      )}
                    </HStack>
                  )}
                  <IconButton
                    aria-label="Archive chat"
                    variant="ghost"
                    bg="transparent"
                    onClick={handleArchiveRoom}
                    color={textColor}
                    size="sm"
                    _hover={{ bg: hoverBg }}
                    title="Archive this chat"
                  >
                    <FaArchive />
                  </IconButton>
                </Flex>
                <ChatMessages
                  messages={messages}
                  selectedRoom={selectedRoom}
                  currentUserId={currentUserId}
                  newMessage={newMessage}
                  onNewMessageChange={setNewMessage}
                  onSendMessage={handleSendMessage}
                  bgColor={bgColor}
                  messageBg={messageBg}
                  userMessageBg={userMessageBg}
                  textColor={textColor}
                  secondaryText={secondaryText}
                  inputBg={inputBg}
                  borderColor={borderColor}
                  PRIMARY_GREEN={PRIMARY_GREEN}
                  isGroupChat={isGroupChat}
                  groupChatMembers={groupChatMembers}
                  isSupportChat={isSupportChat}
                />
              </Box>
            )}
          </MotionBox>
        )}
      </AnimatePresence>

      <GroupChatDialog
        isOpen={isGroupChatOpen}
        onClose={onGroupChatClose}
        users={users}
        onCreateGroupChat={handleCreateGroupChat}
        bgColor={bgColor}
        textColor={textColor}
        secondaryText={secondaryText}
        inputBg={inputBg}
        hoverBg={hoverBg}
        PRIMARY_GREEN={PRIMARY_GREEN}
        rooms={rooms}
        onJoinDirectChat={handleJoinDirectChat}
        createDirectChat={createDirectChat}
      />
      <GroupChatMembersDialog
        isOpen={isGroupChatMembersOpen}
        onClose={() => setIsGroupChatMembersOpen(false)}
        groupChatMembers={groupChatMembers}
      />
    </React.Fragment>
  );
}; 